import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Input,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getAllDirectMessages, getDirectMessagesByUser, createDirectMessage } from '../../services/careCoordinationService';

export default function DirectMessagingTab({ patientId }) {
  const [messages, setMessages] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewMsg, setViewMsg] = useState(null);
  const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '', attachments: [] });

  useEffect(() => {
    if (patientId) {
      Promise.all([
        getDirectMessagesByUser(patientId, 'in'),
        getDirectMessagesByUser(patientId, 'out')
      ]).then(([inbox, outbox]) => setMessages([...inbox, ...outbox]));
    } else {
      getAllDirectMessages().then(setMessages);
    }
  }, [patientId]);

  const handleComposeOpen = () => {
    setComposeForm({ to: '', subject: '', body: '', attachments: [] });
    setComposeOpen(true);
  };
  const handleComposeClose = () => setComposeOpen(false);

  const handleComposeChange = (e) => {
    setComposeForm({ ...composeForm, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setComposeForm({
      ...composeForm,
      attachments: Array.from(e.target.files).map((file, idx) => ({
        id: `upload${idx}-${file.name}`,
        name: file.name,
        type: 'Upload',
      }))
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    await createDirectMessage({
      direction: 'out',
      from: 'system',
      to: composeForm.to,
      subject: composeForm.subject,
      date: new Date().toISOString().slice(0, 10),
      body: composeForm.body,
      attachments: composeForm.attachments,
      read: true
    });
    getAllDirectMessages().then(setMessages);
    setComposeOpen(false);
  };

  const handleView = (msg) => setViewMsg(msg);
  const handleViewClose = () => setViewMsg(null);

  const handleDownload = (attachment) => {
    alert(`Downloading ${attachment.name}`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleComposeOpen}>
          Compose Message
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Attachments</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map((msg) => (
              <TableRow key={msg.id}>
                <TableCell>{msg.from}</TableCell>
                <TableCell>{msg.to}</TableCell>
                <TableCell>{msg.subject}</TableCell>
                <TableCell>{msg.date}</TableCell>
                <TableCell>
                  {msg.attachments && msg.attachments.map(att => (
                    <Tooltip title={att.name} key={att.id}>
                      <IconButton size="small" onClick={() => handleDownload(att)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ))}
                </TableCell>
                <TableCell>{msg.read ? 'Read' : 'Unread'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleView(msg)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Compose Message Modal */}
      <Dialog open={composeOpen} onClose={handleComposeClose} maxWidth="sm" fullWidth>
        <DialogTitle>Compose Message</DialogTitle>
        <form onSubmit={handleSend}>
          <DialogContent>
            <TextField
              margin="dense"
              label="To (Direct Address)"
              name="to"
              fullWidth
              value={composeForm.to}
              onChange={handleComposeChange}
              required
            />
            <TextField
              margin="dense"
              label="Subject"
              name="subject"
              fullWidth
              value={composeForm.subject}
              onChange={handleComposeChange}
              required
            />
            <TextField
              margin="dense"
              label="Message"
              name="body"
              fullWidth
              multiline
              minRows={3}
              value={composeForm.body}
              onChange={handleComposeChange}
            />
            <Box mt={2}>
              <Typography variant="subtitle2">Attachments</Typography>
              <Input
                type="file"
                inputProps={{ multiple: true }}
                onChange={handleFileChange}
              />
              {composeForm.attachments.length > 0 && (
                <Box mt={1}>
                  {composeForm.attachments.map(att => (
                    <Typography key={att.id} variant="body2">{att.name}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleComposeClose}>Cancel</Button>
            <Button type="submit" variant="contained">Send</Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* View Message Modal */}
      <Dialog open={!!viewMsg} onClose={handleViewClose} maxWidth="sm" fullWidth>
        <DialogTitle>Message Details</DialogTitle>
        {viewMsg && (
          <DialogContent>
            <Typography variant="subtitle2">From: {viewMsg.from}</Typography>
            <Typography variant="subtitle2">To: {viewMsg.to}</Typography>
            <Typography variant="subtitle2">Subject: {viewMsg.subject}</Typography>
            <Typography variant="subtitle2">Date: {viewMsg.date}</Typography>
            <Box mt={2} mb={2}>
              <Typography variant="body1">{viewMsg.body}</Typography>
            </Box>
            <Typography variant="subtitle2">Attachments:</Typography>
            {viewMsg.attachments && viewMsg.attachments.map(att => (
              <Box key={att.id} display="flex" alignItems="center">
                <Typography variant="body2" sx={{ mr: 1 }}>{att.name}</Typography>
                <IconButton size="small" onClick={() => handleDownload(att)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 